import React, { useState } from 'react';
import { signInWithEmailAndPassword, signOut, sendPasswordResetEmail } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebaseConfig';
import './Login.css';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState('');
  const navigate = useNavigate();

  const handleForgotPassword = async () => {
    setError('');
    setNotice('');
    if (!email.trim()) {
      setError('Enter your email above first, then tap "Forgot password?"');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setNotice("If an account exists for this email, you'll receive a reset link shortly.");
    } catch (err: any) {
      // Match the app's pattern: never reveal whether an account exists.
      if (err?.code === 'auth/user-not-found') {
        setNotice("If an account exists for this email, you'll receive a reset link shortly.");
      } else if (err?.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (err?.code === 'auth/too-many-requests') {
        setError('Too many requests. Please try again later.');
      } else {
        setError(err?.message || 'Could not send reset email. Please try again.');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      // Force a fresh token so the customer claim (if any) is present, then
      // check it. A contributor with no customer claim is refused right here —
      // signed out, kept on this page, shown the error. They never reach the app.
      const token = await cred.user.getIdTokenResult(true);
      if (token.claims.customer === true) {
        navigate('/dashboard');
      } else {
        await signOut(auth);
        setError('No account exists. Please sign up for a customer account.');
        setLoading(false);
      }
    } catch (err: any) {
      const code = err?.code || '';
      if (
        code === 'auth/invalid-credential' ||
        code === 'auth/wrong-password' ||
        code === 'auth/user-not-found' ||
        code === 'auth/invalid-email'
      ) {
        setError('No account exists with those details. Please check your email and password, or sign up for a customer account.');
      } else if (code === 'auth/too-many-requests') {
        setError('Too many attempts. Please wait a moment and try again.');
      } else {
        setError('Unable to sign in. Please try again.');
      }
      setLoading(false);
    }
  };

  return (
    <div
      className="login-bg"
      style={{
        backgroundImage: 'url(/pix-multilogo-background-white.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <form className="login-container" onSubmit={handleSubmit}>
        <img src="/pix-logo.png" alt="Pixano Logo" className="login-logo" />
        <div className="login-title">Customer<br />Marketplace</div>
        {error && <div className="login-error">{error}</div>}
        {notice && <div className="login-error" style={{ color: '#059669' }}>{notice}</div>}
        <input
          type="email"
          className="login-input"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          type="password"
          className="login-input"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <button
          type="submit"
          className="login-btn"
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Login'}
        </button>
        <button
          type="button"
          className="login-toggle"
          onClick={handleForgotPassword}
          disabled={loading}
        >
          Forgot password?
        </button>
        <div className="login-footer">
          <div style={{ marginBottom: '1rem' }}>
            Need a customer account?{' '}
            <span
              className="login-website-link"
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => navigate('/signup')}
            >
              Sign up
            </span>
          </div>
          <a
            href="https://pixano.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="login-website-link"
          >
            Visit Pixano Website →
          </a>
        </div>
      </form>
    </div>
  );
}
